<!--
Invoice filters logic:
  filterSelect component is a generic filterdropdown component that is reused to display all types of filters.
  Invoice filters have a special case to pass in 'filterGroups' since roles is split into two ('Invoice based', 'PO based' roles).
  onFilterSelected passes the event to this.filterList which is a generic filter that filters on
  all current filters applied and returns a filtered object.
  We check for direct reports in both filter events (when roles are clicked, and when direct report names are clicked).
  Filters are saved in vuex using invoicesTableUserPreferences object and reapplied when user revisits the tab.
-->
<template>

  <div>
    <div class="row" v-if="!invoiceDashboardDataIsLoading">
      <filter-select
          ref="supplierNameFilter"
          :filter-title="'Supplier name'"
          :filter-options="supplierNameList"
          :saved-user-filter="supplierNameSavedFilter"
          @optionsSelected="onFilterSelected($event,INVOICE_FILTER_EVENT_NAMES.INVOICE_SUPPLIER_NAME)"
          @allOptionsCleared="onAllOptionsCleared($event,'supplierNameAllUnchecked')"
          :allOptionsUnchecked="invoicesTableUserPreferences.filter.supplierNameAllUnchecked"
      />
      <filter-select
          ref="statusFilter"
          :filter-title="'Status'"
          :filter-options="statusList"
          :saved-user-filter="statusSavedFilter"
          @optionsSelected="onFilterSelected($event, INVOICE_FILTER_EVENT_NAMES.INVOICE_STATUS)"
          @allOptionsCleared="onAllOptionsCleared($event,'invoiceStatusAllUnchecked')"
          :allOptionsUnchecked="invoicesTableUserPreferences.filter.invoiceStatusAllUnchecked"
      />
      <filter-select
          ref="invoiceTypeFilter"
          :filter-title="'Invoice type'"
          :filter-options="invoiceTypeList"
          :saved-user-filter="invoiceTypeSavedFilter"
          @optionsSelected="onFilterSelected($event, INVOICE_FILTER_EVENT_NAMES.INVOICE_TYPE)"
          @allOptionsCleared="onAllOptionsCleared($event,'invoiceTypeAllUnchecked')"
          :allOptionsUnchecked="invoicesTableUserPreferences.filter.invoiceTypeAllUnchecked"
      />
    </div>
    <div class="row" v-if="!invoiceDashboardDataIsLoading">
      <filter-select
          ref="invoicesRolesFilter"
          :filter-title="'Roles'"
          :filter-groups="INVOICE_ROLES_FILTER_GROUP"
          :filter-options="invoicesRolesList"
          :saved-user-filter="rolesSavedFilter"
          @optionsSelected="onFilterSelected($event, INVOICE_FILTER_EVENT_NAMES.INVOICE_ROLES)"
          @allOptionsCleared="onAllOptionsCleared($event,'rolesAllUnchecked')"
          :allOptionsUnchecked="invoicesTableUserPreferences.filter.rolesAllUnchecked"
      />
      <filter-select v-if="directReportsList && directReportsList.length > 0"
                     ref="invoicesDirectReportsFilter"
                     :filter-title="'Requested by my direct reports'"
                     :filter-options="directReportsList"
                     :saved-user-filter="invoicesDirectReportsSavedFilter"
                     @optionsSelected="onFilterSelected($event, INVOICE_FILTER_EVENT_NAMES.INVOICE_DIRECT_REPORTS)"
                     @allOptionsCleared="onAllOptionsCleared($event,'invoicesDirectReportsAllUnchecked')"
                     :allOptionsUnchecked="invoicesTableUserPreferences.filter.invoicesDirectReportsAllUnchecked"
      />
      <span class="col-lg-3 px-4 py-1 d-flex align-items-center" v-if="showClearFilters"><span @click="clearFilters" class="solution-link">Clear filters</span></span>
    </div>
  </div>
</template>

<script>
import CONSTANTS from "@/store/constants";
import {mapGetters, mapMutations} from "vuex";
import FilterSelect from "@/components/InvoiceAndPurchaseOrders/FilterSelect";
import InvoicesAndPOMixin from "@/mixins/InvoicesAndPOMixin";

export default {
  name: "InvoiceFilters",
  props: ['invoiceDashboardDataIsLoading'],
  mixins: [InvoicesAndPOMixin],
  components: {FilterSelect},
  watch: {
    formattedInvoices() {
      this.clearFilters()
    }
  },
  data() {
    return {
      invoiceTypeData: [
        {
          text: "Purchase order invoices",
          formType: [CONSTANTS.PO_BASED_API_FORM_TYPE, CONSTANTS.ADV_PO_BASED_API_FORM_TYPE, CONSTANTS.ONE_TIME_SUPPLIER_API_FORM_TYPE]
        },
        {
          text: "Non purchase order invoices",
          formType: [CONSTANTS.NON_PO_BASED_API_FORM_TYPE]
        }
      ],
      INVOICE_FILTER_EVENT_NAMES: {
        INVOICE_TYPE: "invoiceType",
        INVOICE_DIRECT_REPORTS: "invoicesDirectReports",
        INVOICE_ROLES: "roles",
        INVOICE_STATUS: "invoiceStatus",
        INVOICE_SUPPLIER_NAME: "supplierName"
      },
      filterSelected: false,
      filteredList: null
    }
  },
  computed: {
    ...mapGetters(['invoicesTableUserPreferences','invoicesRolesList','invoicesDirectReports','formattedInvoices','INVOICES_FILTER_ROLES','currentUser']),
    showClearFilters() {
      return this.invoicesTableUserPreferences.showClearFilters
    },
    INVOICE_ROLES_FILTER_GROUP() {
      return  [this.INVOICES_FILTER_ROLES.NON_PO_INVOICES_FILTER.INVOICES_GROUP_TITLE,this.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.PURCHASE_ORDERS_GROUP_TITLE]
    },
    supplierNameSavedFilter() {
      return this.invoicesTableUserPreferences.filter.supplierName
    },
    statusSavedFilter() {
      return this.invoicesTableUserPreferences.filter.invoiceStatus
    },
    invoiceTypeSavedFilter() {
      return this.invoicesTableUserPreferences.filter.invoiceType
    },
    rolesSavedFilter() {
      return this.invoicesTableUserPreferences.filter.roles
    },
    invoicesDirectReportsSavedFilter() {
      return this.invoicesTableUserPreferences.filter.invoicesDirectReports
    },
    directReportsList() {
      return Array.from(Object.values(this.invoicesDirectReports), reports => reports.fullName)
    },
    invoiceTypeList() {
      return Array.from(Object.values(this.invoiceTypeData), invoice => invoice.text )
    },
    supplierNameList(){
      let supplierNames = []
      if(this.formattedInvoices &&this.formattedInvoices.length>0){
        this.formattedInvoices.forEach(invoice =>{
          if(!supplierNames.includes(invoice.supplierName) && this.isValidString(invoice.supplierName))
            supplierNames.push(invoice.supplierName)
        })
      }
      return supplierNames && supplierNames.length > 0 ? this.sortIgnoreCase(supplierNames) : supplierNames;
    },
    statusList(){
      let statusList = []
      if(this.formattedInvoices &&this .formattedInvoices.length>0){
        this.formattedInvoices.forEach(invoice =>{
          if(!statusList.includes(invoice.invoiceStatus) && this.isValidString(invoice.invoiceStatus))
            statusList.push(invoice.invoiceStatus)
        })
      }
      return statusList && statusList.length > 0 ? this.sortIgnoreCase(statusList) : statusList;
    },
  },
  methods: {
    ...mapMutations(['setInvoicesTableUserPreferences']),
    filterByInvoiceType(filterBy, selectedFilter) {
      let filterByNonPO = 'Non purchase order invoices'
      let nonPODocType = this.invoiceTypeData.find(data => data.text === filterByNonPO).formType
      /*
        invoiceTypeData is a static object with two items.
        if selectedFilter is less than invoiceTypeData, we can proceed with nonPODocType check
        else we filter on PO based forms.
      */
      if(selectedFilter.length  < this.invoiceTypeData.length) {
        if(selectedFilter[0] === filterByNonPO) {
          return filterBy && filterBy.docType && nonPODocType.includes(filterBy.docType)
        } else {
          return filterBy && filterBy.docType && !nonPODocType.includes(filterBy.docType)
        }
      } else return true
    },
    updateInvoicesFilterCriteria() {
      let filterObj = {}
      //Populate filterObj with keys specific to filter criteria and ignore empty arrays or null keys
      if(this.filtersExist()) {
        for (let [filterName, filterValues] of Object.entries(this.invoicesTableUserPreferences.filter)) {
          if(filterValues && filterValues.length > 0) {
            filterObj[filterName] = JSON.parse(JSON.stringify(filterValues))
          }
        }
      } else if(this.$refs.invoicesRolesFilter && this.$refs.invoicesRolesFilter.selectedOptions.length > 0) {
        filterObj.roles = this.$refs.invoicesRolesFilter.selectedOptions
      }
      return filterObj
    },
    filtersExist() {
      let filtersArrayObj = this.invoicesTableUserPreferences.filter
      return filtersArrayObj.invoiceStatus.length > 0
              || filtersArrayObj.invoiceType.length > 0
              || filtersArrayObj.invoicesDirectReports.length > 0
              || filtersArrayObj.roles.length > 0
              || filtersArrayObj.supplierName.length > 0
    },
    filterInvoicesSubmittedByMe(invoice, userId, thatInstance) {
      return thatInstance.selectedRolesContain(thatInstance.INVOICES_FILTER_ROLES.NON_PO_INVOICES_FILTER.INVOICES_SUBMITTED_BY_ME_STRING) && userId === invoice.requesterId
    },
    selectedRolesContain(role) {
      let selectedRoles = this.$refs.invoicesRolesFilter.selectedOptions
      return selectedRoles.indexOf(role) !== -1
    },
    filterPORequestedByMe(invoice, userId, thatInstance) {
      return thatInstance.selectedRolesContain(thatInstance.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_PO_REQUESTED_BY_ME)
          && invoice
          && invoice.purchaseOrders
          && invoice.purchaseOrders.some(purchaseOrders => purchaseOrders && purchaseOrders.requestedBy && purchaseOrders.requestedBy === userId)
    },
    filterPOCreatedByMe(invoice, userId, thatInstance) {
      return thatInstance.selectedRolesContain(thatInstance.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_PO_CREATED_BY_ME)
          && invoice
          && invoice.purchaseOrders
          && invoice.purchaseOrders.some(purchaseOrders => purchaseOrders && purchaseOrders.createdBy && purchaseOrders.createdBy === userId)
    },
    filterPOCostCenterOwner(invoice, userId, thatInstance) {
      return thatInstance.selectedRolesContain(thatInstance.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_MY_COST_CENTER_PO)
          && invoice
          && invoice.purchaseOrders
          && invoice.purchaseOrders.some(purchaseOrders => purchaseOrders && purchaseOrders.poAdditionalData && purchaseOrders.poAdditionalData.some(data => data.costCenterOwner === userId))
    },
    filterPOCostCenterFBP(invoice, userId, thatInstance) {
      return thatInstance.selectedRolesContain(thatInstance.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_COST_CENTER_FBP_PO)
          && invoice
          && invoice.purchaseOrders
          && invoice.purchaseOrders.some(purchaseOrders => purchaseOrders && purchaseOrders.poAdditionalData && purchaseOrders.poAdditionalData.some(data => data.costCenterFbp === userId))
    },
    filterByDirectReports(invoice) {
      let isDirectReportRequester = false
      let selectedUsers = this.$refs.invoicesDirectReportsFilter ? this.$refs.invoicesDirectReportsFilter.selectedOptions : []
      if(selectedUsers && selectedUsers.length > 0 && this.selectedRolesContain(this.INVOICES_FILTER_ROLES.NON_PO_INVOICES_FILTER.INVOICES_SUBMITTED_BY_MY_DIRECT_REPORTS)) {
        selectedUsers.forEach((report) => {
          let reportObj = this.invoicesDirectReports.find(user => user.fullName === report)
          if(reportObj && reportObj.id && reportObj.id === invoice.requesterId) {
            isDirectReportRequester = true
          }
        })
      }
      return isDirectReportRequester
    },
    filterByPORequestedBy(invoice) {
      let isDirectReportRequester = false
      let selectedUsers = this.$refs.invoicesDirectReportsFilter ? this.$refs.invoicesDirectReportsFilter.selectedOptions : []
      if(selectedUsers && selectedUsers.length > 0 && this.selectedRolesContain(this.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_PO_SUBMITTED_BY_MY_DIRECT_REPORTS)) {
        selectedUsers.forEach((report) => {
          let reportObj = this.invoicesDirectReports.find(user => user.fullName === report)
          if(reportObj && reportObj.id && invoice && invoice.purchaseOrders  && invoice.purchaseOrders.some((po => po && po.requestedBy && po.requestedBy === reportObj.id))) {
            isDirectReportRequester = true
          }
        })
      }
      return isDirectReportRequester
    },
    filterByPOCreatedBy(invoice) {
      let isDirectReportRequester = false
      let selectedUsers = this.$refs.invoicesDirectReportsFilter ? this.$refs.invoicesDirectReportsFilter.selectedOptions : []
      if(selectedUsers && selectedUsers.length > 0 && this.selectedRolesContain(this.INVOICES_FILTER_ROLES.PO_INVOICES_FILTER.INVOICES_FOR_PO_CREATED_BY_MY_DIRECT_REPORTS)) {
        selectedUsers.forEach((report) => {
          let reportObj = this.invoicesDirectReports.find(user => user.fullName === report)
          if(reportObj && reportObj.id && invoice && invoice.purchaseOrders && invoice.purchaseOrders.some((po => po && po.createdBy && po.createdBy === reportObj.id))) {
            isDirectReportRequester = true
          }
        })
      }
      return isDirectReportRequester
    },
    onFilterSelected(filteredItems, filteredVariable) {
      if(filteredItems && filteredItems.length>0){
        this.emitFiltersSelected(true)
        this.invoicesTableUserPreferences.filter[filteredVariable] = filteredItems
        this.invoicesTableUserPreferences.showClearFilters = true
      }else{
        this.filteredList = [];
        this.emitInvoicesFilteredListUpdate()
        this.invoicesTableUserPreferences.filter[filteredVariable] = []
        this.emitFiltersSelected(false)
      }
      this.setInvoicesTableUserPreferences(this.invoicesTableUserPreferences)
      this.filterList()
    },
    filterList() {
      this.emitFiltersSelected(true)
      if(this.isFilterAllUnchecked()) {
        this.filteredList = []
        this.emitInvoicesFilteredListUpdate()
      } else {
        //Filter by union of selected filters which are in memory.
        let selectedFilters = this.updateInvoicesFilterCriteria()
        this.$hhMixpanel.trackEvent("Supplier central - Invoices filters selected", {filters : Object.keys(selectedFilters)});

        //When using filter we need local scope of this so that the sub-methods can use the same instance while rendering filteredData.
        //Using thatInstance as a local scope variable for 'this'.
        let thatInstance = this

        if(this.formattedInvoices && this.formattedInvoices.length > 0) {
          this.filteredList = this.formattedInvoices.filter(function (invoice) {
            return Object.keys(selectedFilters).every(function (filter) {
              if(filter === thatInstance.INVOICE_FILTER_EVENT_NAMES.INVOICE_TYPE) {
                return thatInstance.filterInvoicesByInvoiceType(selectedFilters, filter, invoice, thatInstance)
              } else if(filter === thatInstance.INVOICE_FILTER_EVENT_NAMES.INVOICE_DIRECT_REPORTS
                  || filter === thatInstance.INVOICE_FILTER_EVENT_NAMES.INVOICE_ROLES) {
                return thatInstance.filterInvoicesByRolesAndDirectReports(invoice, thatInstance)
              } else {
                return selectedFilters[filter].some(function (value) {
                  return invoice[filter] === value;
                });
              }
            });
          });
          this.emitInvoicesFilteredListUpdate()
        }
      }
    },
    isFilterAllUnchecked() {
      return this.invoicesTableUserPreferences.filter.supplierNameAllUnchecked
            || this.invoicesTableUserPreferences.filter.invoiceStatusAllUnchecked
            || this.invoicesTableUserPreferences.filter.invoiceTypeAllUnchecked
            || this.invoicesTableUserPreferences.filter.rolesAllUnchecked
    },
    /* below method returns as soon as a match is found for either of the filter criteria methods. */
    filterInvoicesByRolesAndDirectReports(invoice, thatInstance) {
      let userId = this.viewingAPUserDataFor ? this.apUserId : this.currentUser.userName
      return thatInstance.filterInvoicesSubmittedByMe(invoice, userId, thatInstance)
                || thatInstance.filterPORequestedByMe(invoice, userId, thatInstance)
                || thatInstance.filterPOCreatedByMe(invoice, userId, thatInstance)
                || thatInstance.filterPOCostCenterOwner(invoice, userId, thatInstance)
                || thatInstance.filterPOCostCenterFBP(invoice, userId, thatInstance)
                || thatInstance.filterByDirectReports(invoice)
                || thatInstance.filterByPORequestedBy(invoice)
                || thatInstance.filterByPOCreatedBy(invoice)
    },
    filterInvoicesByInvoiceType(selectedFilters, filter, invoice, thatInstance) {
      return selectedFilters[filter].some(function () {
        return thatInstance.filterByInvoiceType(invoice, selectedFilters[filter])
      });
    },
    clearFilters() {
      this.filteredList = []
      this.emitInvoicesFilteredListUpdate()
      if (this.$refs.supplierNameFilter){
        this.$refs.supplierNameFilter.markAllSelected()
      }
      if (this.$refs.statusFilter){
        this.$refs.statusFilter.markAllSelected()
      }
      if(this.$refs.invoiceTypeFilter) {
        this.$refs.invoiceTypeFilter.markAllSelected()
      }
      if(this.$refs.invoicesRolesFilter) {
        this.$refs.invoicesRolesFilter.markAllSelected()
      }
      if(this.$refs.invoicesDirectReportsFilter) {
        this.$refs.invoicesDirectReportsFilter.markAllSelected()
      }
      this.$emit('onResetInvoiceFilterPreferences')
      this.emitFiltersSelected(false)
      this.filterList()
    },
    onAllOptionsCleared(event, params) {
      this.invoicesTableUserPreferences.filter[params] = event.status
      if(event.filterTitle !== 'Roles' && event.filterTitle !== 'Invoice type') {
        this.invoicesTableUserPreferences.showClearFilters = true
      }
      this.filterList()
    },
    emitFiltersSelected(filtersSelected) {
      this.$emit('onFiltersSelected', filtersSelected)
    },
    emitInvoicesFilteredListUpdate() {
      this.$emit('onFilterListUpdated', this.filteredList)

    }
  }
}

</script>